import { computed, ref } from 'vue'

export type VirtualKeyboardLayout =
  | 'characters'
  | 'alphanumeric'
  | 'specialsymbols'
  | 'numeric'
  | 'numericthreeline'
  | 'decimal'

const isEnabled = ref(false)
const isVisible = ref(false)
const activeElement = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
const layout = ref<VirtualKeyboardLayout>('characters')
const isShiftEnabled = ref(false)
const anchorSide = ref<'left' | 'right'>('left')

function dispatchInputEvent(target: HTMLInputElement | HTMLTextAreaElement) {
  target.dispatchEvent(new Event('input', { bubbles: true }))
  target.dispatchEvent(new Event('change', { bubbles: true }))
}

function resolveLayout(target: HTMLInputElement | HTMLTextAreaElement): VirtualKeyboardLayout {
  const explicit = target.dataset.keyboard
  if (
    explicit === 'characters' ||
    explicit === 'alphanumeric' ||
    explicit === 'specialsymbols' ||
    explicit === 'numeric' ||
    explicit === 'numericthreeline' ||
    explicit === 'decimal'
  ) {
    return explicit
  }

  const inputMode = `${target.getAttribute('inputmode') || ''}`.toLowerCase()
  if (inputMode === 'numeric') return 'numericthreeline'
  if (inputMode === 'decimal') return 'decimal'

  if (target instanceof HTMLInputElement) {
    const type = `${target.type || ''}`.toLowerCase()
    if (type === 'number' || type === 'tel') return 'numericthreeline'
  }

  const identifier = `${target.name || ''} ${target.id || ''} ${target.className || ''}`.toLowerCase()
  if (
    identifier.includes('stake') ||
    identifier.includes('amount') ||
    identifier.includes('price') ||
    identifier.includes('cost')
  ) {
    return 'decimal'
  }

  if (
    identifier.includes('booking') ||
    identifier.includes('draw') ||
    identifier.includes('code') ||
    identifier.includes('reference')
  ) {
    return 'alphanumeric'
  }

  return 'characters'
}

function computeAnchorSide(target: HTMLInputElement | HTMLTextAreaElement): 'left' | 'right' {
  const rect = target.getBoundingClientRect()
  return rect.left < window.innerWidth / 2 ? 'left' : 'right'
}

function ensureFocus() {
  activeElement.value?.focus()
}

function setActiveElement(target: HTMLInputElement | HTMLTextAreaElement | null) {
  activeElement.value = target
  if (!target || !isEnabled.value) {
    isVisible.value = false
    isShiftEnabled.value = false
    return
  }

  layout.value = resolveLayout(target)
  anchorSide.value = computeAnchorSide(target)
  isVisible.value = true
}

function insertText(text: string) {
  const target = activeElement.value
  if (!target) return

  ensureFocus()
  const selectionStart = target.selectionStart ?? target.value.length
  const selectionEnd = target.selectionEnd ?? selectionStart
  const nextText = isShiftEnabled.value ? text.toUpperCase() : text
  target.setRangeText(nextText, selectionStart, selectionEnd, 'end')
  dispatchInputEvent(target)

  if (layout.value === 'characters' && isShiftEnabled.value) {
    isShiftEnabled.value = false
  }
}

function appendIncrement(amount: number) {
  const target = activeElement.value
  if (!target) return
  const current = parseFloat(target.value || '0')
  const next = Number.isFinite(current) ? current + amount : amount
  target.value = Number.isInteger(next) ? String(next) : next.toFixed(2).replace(/\.00$/, '')
  dispatchInputEvent(target)
}

function backspace() {
  const target = activeElement.value
  if (!target) return

  ensureFocus()
  const start = target.selectionStart ?? target.value.length
  const end = target.selectionEnd ?? start

  if (start !== end) {
    target.setRangeText('', start, end, 'end')
  } else if (start > 0) {
    target.setRangeText('', start - 1, start, 'end')
  }

  dispatchInputEvent(target)
}

function clearValue() {
  const target = activeElement.value
  if (!target) return
  target.value = ''
  dispatchInputEvent(target)
}

function closeKeyboard() {
  isVisible.value = false
  isShiftEnabled.value = false
  activeElement.value?.blur()
  activeElement.value = null
}

function setLayout(nextLayout: VirtualKeyboardLayout) {
  layout.value = nextLayout
}

function toggleShift() {
  isShiftEnabled.value = !isShiftEnabled.value
}

function setKeyboardEnabled(enabled: boolean) {
  isEnabled.value = enabled
  if (!enabled) {
    closeKeyboard()
  }
}

export function useVirtualKeyboard() {
  return {
    isEnabled: computed(() => isEnabled.value),
    isVisible: computed(() => isVisible.value),
    layout: computed(() => layout.value),
    isShiftEnabled: computed(() => isShiftEnabled.value),
    activeElement: computed(() => activeElement.value),
    anchorSide: computed(() => anchorSide.value),
    setKeyboardEnabled,
    setActiveElement,
    setLayout,
    insertText,
    appendIncrement,
    backspace,
    clearValue,
    closeKeyboard,
    toggleShift,
  }
}
